import { Card, Table, Button, Tag, Spin } from 'antd';
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import {
	getVirtualTransaction,
	clearVirtualTransactionResult,
} from '../../methods/redux/reducers/payment-gateway';

const StyledCard = styled(Card)`
	.ant-card-body {
		padding: 24px;
	}
`;

const StatusTag = styled(Tag)`
	font-weight: 500;
	padding: 4px 12px;
	border-radius: 4px;
`;

const StyledTable = styled(Table)`
	.table-row-light {
		background-color: #ffffff;
	}
	.table-row-dark {
		background-color: #fafafa;
	}
	.ant-table-tbody > tr:hover > td {
		background-color: #e6f7ff !important;
	}
`;

const SafeHavenVirtualTransactionDetails = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { virtualAccountId } = useParams<{ virtualAccountId: string }>();
	const { virtualTransactionResult } = useAppSelector(
		(store) => store.paymentGateway
	) as { virtualTransactionResult: any };

	useEffect(() => {
		if (virtualAccountId) {
			dispatch(getVirtualTransaction(virtualAccountId));
		}
		return () => {
			dispatch(clearVirtualTransactionResult());
		};
	}, [virtualAccountId, dispatch]);

	const handleBack = () => {
		navigate('/core-modules/virtual-fiat-accounts');
	};

	if (!virtualTransactionResult) {
		return (
			<StyledCard>
				<div style={{ textAlign: 'center', padding: '50px' }}>
					<Spin size="large" />
					<p style={{ marginTop: '16px' }}>
						Loading virtual transaction details...
					</p>
				</div>
			</StyledCard>
		);
	}

	const transactionData = virtualTransactionResult.data?.data;

	// Prepare table data
	const tableData = [
		{
			key: 'id',
			label: 'ID',
			value: transactionData?._id || 'N/A',
			type: 'text',
		},
		{
			key: 'sessionId',
			label: 'Session ID',
			value: transactionData?.sessionId || 'N/A',
			type: 'text',
		},
		{
			key: 'amount',
			label: 'Amount',
			value: transactionData?.amount
				? new Intl.NumberFormat('en-NG', {
						style: 'currency',
						currency: 'NGN',
				  }).format(transactionData.amount)
				: 'N/A',
			type: 'currency',
		},
		{
			key: 'fees',
			label: 'Fees',
			value: transactionData?.fees
				? new Intl.NumberFormat('en-NG', {
						style: 'currency',
						currency: 'NGN',
				  }).format(transactionData.fees)
				: 'N/A',
			type: 'currency',
		},
		{
			key: 'status',
			label: 'Status',
			value: transactionData?.status || 'N/A',
			type: 'status',
		},
		{
			key: 'senderName',
			label: 'Sender Name',
			value: transactionData?.senderName || 'N/A',
			type: 'text',
		},
		{
			key: 'senderAccountNumber',
			label: 'Sender Account Number',
			value: transactionData?.senderAccountNumber || 'N/A',
			type: 'text',
		},
		{
			key: 'senderBankCode',
			label: 'Sender Bank Code',
			value: transactionData?.senderBankCode || 'N/A',
			type: 'text',
		},
		{
			key: 'narration',
			label: 'Narration',
			value: transactionData?.narration || 'N/A',
			type: 'text',
		},
		{
			key: 'createdAt',
			label: 'Created At',
			value: transactionData?.createdAt
				? new Date(transactionData.createdAt).toLocaleString()
				: 'N/A',
			type: 'date',
		},
	];

	const columns = [
		{
			title: 'Field',
			dataIndex: 'label',
			key: 'label',
			width: '30%',
			render: (text: string) => (
				<strong style={{ color: '#1890ff' }}>{text}</strong>
			),
		},
		{
			title: 'Value',
			dataIndex: 'value',
			key: 'value',
			width: '70%',
			render: (text: string, record: any) => {
				if (record.type === 'status') {
					const statusColors: any = {
						success: 'green',
						pending: 'orange',
						failed: 'red',
						completed: 'green',
						processing: 'blue',
						cancelled: 'default',
					};
					const color = statusColors[text?.toLowerCase()] || 'default';
					return (
						<StatusTag color={color}>{text?.toUpperCase() || 'N/A'}</StatusTag>
					);
				}
				return <span style={{ wordBreak: 'break-word' }}>{text}</span>;
			},
		},
	];

	return (
		<StyledCard>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '24px',
				}}
			>
				<div>
					<h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>
						Safe Haven Virtual Transaction Details
					</h1>
					<p style={{ margin: '8px 0 0 0', color: '#666' }}>
						Virtual Account ID: {virtualAccountId}
					</p>
				</div>
				<Button onClick={handleBack}>Back to List</Button>
			</div>

			{(virtualTransactionResult as any)?.status && (
				<div
					style={{
						marginBottom: '24px',
						padding: '12px',
						background: '#f0f8ff',
						borderRadius: '4px',
					}}
				>
					<p style={{ margin: '4px 0' }}>
						<strong>Response Status:</strong>{' '}
						<Tag
							color={
								(virtualTransactionResult as any).status === 'success'
									? 'green'
									: 'orange'
							}
						>
							{(virtualTransactionResult as any).status}
						</Tag>
					</p>
					{(virtualTransactionResult as any)?.status_code && (
						<p style={{ margin: '4px 0' }}>
							<strong>Status Code:</strong>{' '}
							{(virtualTransactionResult as any).status_code}
						</p>
					)}
					{(virtualTransactionResult as any)?.data?.message && (
						<p style={{ margin: '4px 0' }}>
							<strong>Message:</strong>{' '}
							{(virtualTransactionResult as any).data.message}
						</p>
					)}
				</div>
			)}

			<StyledTable
				columns={columns}
				dataSource={tableData}
				pagination={false}
				bordered
				style={{
					backgroundColor: '#fff',
					borderRadius: '8px',
					overflow: 'hidden',
				}}
				rowClassName={(record, index) =>
					index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
				}
			/>
		</StyledCard>
	);
};

export default SafeHavenVirtualTransactionDetails;
